<template>
  <div id="tokenization" @click="versionDropdownIsActive && (versionDropdownIsActive = false)">
    <div class="columns">

      <a
          role="button"
          class="navbar-burger"
          :class="{ 'is-active': menuOpen }"
          aria-label="menu"
          aria-expanded="false"
          data-target="navbar-menu"
          @click="toggleMenu"
          :style="`margin-left: ${menuOpen ? '310px' : 0};`"
      >
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
      </a>

      <section class="sidebar column is-one-quarter">
        <div class="sidebar-content is-fixed is-light is-fullheight" :class="{ 'is-active': menuOpen }">
          <aside class="menu">
            <div class="dropdown is-right" :class="{ 'is-active': versionDropdownIsActive }">
              <div class="dropdown-trigger ml-2">
                <span class="level">
                  <span class="level-item mr-2">Library version</span>
                  <button
                      class="button is-small level-item"
                      aria-haspopup="true"
                      aria-controls="dropdown-menu"
                      @click.stop="versionDropdownIsActive = !versionDropdownIsActive"
                  >
                    <span>{{ version }} </span>
                    <span class="icon is-small">
                      <svg-icon type="mdi" :path="icons.mdiChevronDown"/>
                    </span>
                  </button>
                </span>
              </div>
              <div class="dropdown-menu" id="dropdown-menu" role="menu">
                <div class="dropdown-content" @click.stop="versionDropdownIsActive = false">
                  <router-link v-for="_version of versions" :to="`/tokenization/${_version}`"
                               class="dropdown-item pr-4">
                    {{ _version }}
                  </router-link>
                </div>
              </div>
            </div>
            <hr class="hr"/>

            <p class="menu-label">Setup</p>
            <ul class="menu-list">
              <li v-for="entry in menu">
                <router-link :to="entry.link">{{ entry.title }}</router-link>
              </li>
            </ul>

            <p class="menu-label">API</p>
            <ul class="menu-list">
              <li v-for="item in api">
                <router-link
                    :to="`#${item.code}`"
                    class="code is-size-5"
                    :class="{ 'is-active': activeMenuItem === item.code }"
                    @click="activeMenuItem = item.code, activeSubMenuItem = null"
                >
                  {{ item.code }}
                </router-link>
                <ul v-show="activeMenuItem === item.code">
                  <template v-for="child in item.children">
                    <li v-if="!Array.isArray(child.code)">
                      <router-link
                          :to="`#${child.code}`"
                          class="code is-size-6"
                          :class="{ 'is-active': activeSubMenuItem === child.code }"
                          @click="activeSubMenuItem = child.code"
                      >
                        {{ child.code }}
                      </router-link>
                    </li>
                    <li v-else v-for="code in child.code">
                      <router-link
                          :to="`#${code}`"
                          class="code is-size-6"
                          :class="{ 'is-active': activeSubMenuItem === code }"
                          @click="activeSubMenuItem = code"
                      >
                        {{ code }}
                      </router-link>
                    </li>
                    <template v-if="child.children" v-for="grandchild of child.children">
                      <li v-for="grandchild of child.children">
                        <router-link :to="`#${grandchild.code}`" class="code is-size-7 ml-5">
                          {{ grandchild.code }}
                        </router-link>
                      </li>
                    </template>
                  </template>
                </ul>
              </li>
            </ul>
          </aside>
        </div>
      </section>

      <component
          :is="content"
          :api="api"
          :sampleCode="sampleCode"
          :branding="branding"
          :tokenizationDomain="tokenizationDomain"
      />
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent, markRaw } from 'vue';
import SvgIcon from '@jamescoyle/vue-icon';
import { mdiChevronDown } from '@mdi/js';

export default {
  components: {
    SvgIcon,
  },
  data() {
    return {
      menuOpen: false,
      versionDropdownIsActive: false,
      activeMenuItem: null,
      activeSubMenuItem: null,
      content: null,
      api: null,
      menu: null,
      sampleCode: null,
      versions: [
        'v0.3',
        'v0.2',
        'v0.1',
      ],
      icons: {
        mdiChevronDown,
      },
    };
  },
  created() {
    this.loadData(this.version);
  },
  methods: {
    async loadData(version) {
      this.toggleMenu();

      const [{ api, menu }, { sampleCode }] = await Promise.all([
        import(/* @vite-ignore */ `./${version}/api.js`),
        import(/* @vite-ignore */ `./${version}/sample-code.js`),
      ]);

      this.api = api;
      this.menu = menu;
      this.sampleCode = sampleCode;
      this.content = markRaw(defineAsyncComponent(() => import(/* @vite-ignore */ `./${version}/content.vue`)));
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
  },
  watch: {
    '$route.params.version'(to) {
      return this.loadData(to);
    },
  },
  computed: {
    version() {
      return this.$route.params?.version;
    },
    branding() {
      return this.$root.branding || {};
    },
    tokenizationDomain() {
      return `tokenization.sandbox.${this.branding.baseDomain}`;
    },
  },
};
</script>

<style lang="scss">
@use 'bulma/sass/utilities/mixins';

#tokenization {
  .menu-label {
    margin-left: 10px;
  }

  .hr {
    background-color: silver;
    margin: 20px 0;
  }

  .dropdown-menu, .dropdown-item {
    min-width: fit-content;
    max-width: fit-content;
  }

  li .code {
    font-family: monospace;
  }

  div.content {
    margin-left: 50px;
  }

  div.content:last-of-type {
    margin-bottom: 25px;
  }

  .method-wrapper {
    margin-left: 50px;
  }

  .navbar-burger {
    position: fixed;
  }

  .sidebar .menu {
    margin-top: 25px;
  }

  .sidebar-content.is-fixed {
    margin-top: 54px;
  }

  @include mixins.mobile {
    .sidebar-content:not(.is-active) {
      display: none !important;
    }
  }

  pre {
    margin: 20px 3rem 20px 0;
  }

  .bold {
    font-weight: bold;
  }

  /* b-sidebar */
  .sidebar-content.is-fullheight {
    height: 100%;
    max-height: 100%;
    overflow: hidden;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-content: stretch;
  }

  .sidebar-content.is-fixed {
    position: fixed;
    left: 0;
    top: 0;
  }

  .sidebar-content.is-light {
    background-color: whitesmoke;
  }

  .sidebar-content.is-fixed {
    margin-top: 54px;
  }

  .sidebar-content {
    background-color: whitesmoke;
    box-shadow: 5px 0 13px 3px rgb(10 10 10 / 10%);
    width: 310px;
  }

  /* b menu-list*/
  .menu-list a.is-active {
    background-color: #7957d5;
    color: #fff;
  }

  .menu-list a {
    border-radius: 2px;
    color: #4a4a4a;
    display: block;
    padding: 0.5em 0.75em;
  }
}
</style>
